import { configMgmtControllerListRiskCategoryFeeComponents } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { DataTable, type DataTableColumn } from '../../../components/ui/data-table';
import { Price } from '../../../components/ui/price';
import { usePermissions } from '../../../hooks/use-permissions';
import type { PermitDetailsData } from './PermitDetailsStep';

interface FeeBreakdownItem {
  id: string;
  feeComponentType: { id: string; name: string; code: string };
  payee?: string | null;
  amount: string;
  currency: string;
}

interface ReviewData {
  accountName: string;
  contactName: string;
  details: PermitDetailsData;
}

interface Props {
  data: ReviewData;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-xs">{value}</span>
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  ENV: 'Environmental',
  WASTE: 'Waste',
  VEH: 'Vehicle',
};

const FACILITY_LABELS: Record<string, string> = {
  NEW_FACILITY: 'New Facility',
  EXISTING_FACILITY: 'Existing Facility',
};

export function ReviewStep({ data }: Props) {
  const { canViewField } = usePermissions();
  const { accountName, contactName, details } = data;

  const canViewFee = canViewField('permit', 'feeBreakdown');
  const feeEnabled = canViewFee && !!details.permitTypeId && !!details.riskId;

  const {
    data: feeComponents,
    isFetching: feeFetching,
    isError: feeError,
  } = useQuery<FeeBreakdownItem[]>({
    queryKey: ['risk-fee-components', details.permitTypeId, details.riskId],
    queryFn: () =>
      configMgmtControllerListRiskCategoryFeeComponents({
        id: details.permitTypeId,
        riskId: details.riskId,
      }) as unknown as Promise<FeeBreakdownItem[]>,
    enabled: feeEnabled,
  });

  const totalFee = feeComponents?.reduce((sum, fc) => sum + Number(fc.amount), 0) ?? 0;

  return (
    <div className="space-y-6">
      {/* Account & Contact */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Account & Contact
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white px-4 py-1">
          <Row label="Account" value={accountName} />
          <Row label="Contact" value={contactName} />
        </div>
      </div>

      {/* Permit details */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Permit Details
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white px-4 py-1">
          <Row label="Type" value={TYPE_LABELS[details.permitTypeCode] ?? details.permitTypeCode} />
          {details.requestType && (
            <Row
              label="Request Type"
              value={details.requestType === 'NEW' ? 'New Request' : 'Renewal Request'}
            />
          )}
          {details.facilitySubType && (
            <Row
              label="Facility"
              value={FACILITY_LABELS[details.facilitySubType] ?? details.facilitySubType}
            />
          )}
          {details.riskCode && (
            <Row
              label={details.permitTypeCode === 'ENV' ? 'Risk Level' : 'Category'}
              value={details.riskCode}
            />
          )}
          {details.licenseType && <Row label="License Type" value={details.licenseType} />}
          {details.permitActivity && <Row label="Activity" value={details.permitActivity} />}
          {details.envPermitGeneralComments && (
            <Row label="Comments" value={details.envPermitGeneralComments} />
          )}
          {details.wasteClassification && (
            <Row label="Waste Classification" value={details.wasteClassification} />
          )}
          {details.wasteType && <Row label="Type of Waste" value={details.wasteType} />}
          {details.wasteQuantity && <Row label="Quantity" value={details.wasteQuantity} />}
          {details.landfillLocation && (
            <Row label="Landfill Location" value={details.landfillLocation} />
          )}
          {details.isRenewalOfId && <Row label="Renewing" value={details.isRenewalOfId} />}
          {details.permitTypeCode === 'VEH' && (
            <>
              <Row label="Vehicles" value={String(details.vehicleCount)} />
              {details.vehicleLicenses.map(
                (l, i) =>
                  l.licenseNumber && (
                    <Row
                      key={i}
                      label={`Vehicle ${i + 1}`}
                      value={`${l.licenseNumber}${l.placeOfIssue ? ` — ${l.placeOfIssue}` : ''}`}
                    />
                  ),
              )}
            </>
          )}
        </div>
      </div>

      {/* Fee breakdown */}
      {feeEnabled && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Fee Breakdown
          </h3>
          {feeFetching && (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 size={18} className="animate-spin mr-2" />
              <span className="text-sm">Loading fees…</span>
            </div>
          )}
          {feeError && (
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              Unable to load fee breakdown. Check permit type configuration.
            </div>
          )}
          {feeComponents && !feeFetching && (
            <FeeBreakdownTable items={feeComponents} totalFee={totalFee} />
          )}
        </div>
      )}
    </div>
  );
}

const feeColumns: DataTableColumn<FeeBreakdownItem>[] = [
  {
    key: 'feeName',
    header: 'Fee Name',
    render: (item) => <span className="text-gray-700">{item.feeComponentType.name}</span>,
  },
  {
    key: 'payee',
    header: 'Payee',
    render: (item) => <span className="text-gray-500">{item.payee ?? '—'}</span>,
  },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (item) => <Price value={item.amount} className="text-gray-900" />,
  },
];

function FeeBreakdownTable({ items, totalFee }: { items: FeeBreakdownItem[]; totalFee: number }) {
  return (
    <DataTable
      columns={feeColumns}
      data={items}
      keyField="id"
      tableFooter={
        <tr className="border-t-2 border-gray-200 bg-gray-50">
          <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan={2}>
            Total
          </td>
          <td className="px-4 py-3 text-right text-xs font-bold text-green-700">
            <Price value={totalFee} />
          </td>
        </tr>
      }
    />
  );
}
